<script setup>
import { computed, reactive, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  BIO: { field: 'additionalAttributes.description' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  EMAIL_ADDRESS: { field: 'email' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

function splitHispanicName(nameStr) {
  if (!nameStr) return { firstName: '', lastName: '' };
  const parts = nameStr.trim().split(/\s+/);
  if (parts.length >= 4) {
    return {
      firstName: parts.slice(0, 2).join(' '),
      lastName: parts.slice(2).join(' '),
    };
  }
  if (parts.length === 3) {
    return {
      firstName: parts.slice(0, 2).join(' '),
      lastName: parts[2],
    };
  }
  if (parts.length === 2) {
    return {
      firstName: parts[0],
      lastName: parts[1],
    };
  }
  return {
    firstName: parts[0] || '',
    lastName: '',
  };
}

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitHispanicName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const identityPrefix = computed({
  get: () => {
    const desc = state.additionalAttributes.description || '';
    const match = desc.match(/^([A-Z]-)/);
    return match ? match[1] : 'V-';
  },
  set: async (val) => {
    const desc = state.additionalAttributes.description || '';
    const match = desc.match(/^([A-Z]-)/);
    const number = match ? desc.slice(2) : desc;
    if (number) {
      state.additionalAttributes.description = `${val}${number}`;
      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    }
  }
});

const showIdentityDropdown = ref(false);

const identityOptions = [
  { value: 'V-', label: 'V', action: 'identityPrefix' },
  { value: 'E-', label: 'E', action: 'identityPrefix' },
  { value: 'J-', label: 'J', action: 'identityPrefix' },
  { value: 'G-', label: 'G', action: 'identityPrefix' },
  { value: 'P-', label: 'P', action: 'identityPrefix' },
];

const toggleIdentityDropdown = () => {
  showIdentityDropdown.value = !showIdentityDropdown.value;
};

const closeIdentityDropdown = () => {
  showIdentityDropdown.value = false;
};

const onSelectIdentity = (item) => {
  if (item && item.value) {
    identityPrefix.value = item.value;
    showIdentityDropdown.value = false;
  }
};

const identityNumber = computed({
  get: () => {
    const desc = state.additionalAttributes.description || '';
    const match = desc.match(/^([A-Z]-)/);
    return match ? desc.slice(2) : desc;
  },
  set: async (val) => {
    if (!val) {
      state.additionalAttributes.description = '';
    } else {
      const desc = state.additionalAttributes.description || '';
      const match = desc.match(/^([A-Z]-)/);
      const currentPrefix = match ? match[1] : 'V-';
      state.additionalAttributes.description = `${currentPrefix}${val}`;
    }
    const isFormValid = await v$.value.$validate();
    if (isFormValid) {
      const { firstName, lastName, ...stateWithoutNames } = state;
      emit('update', stateWithoutNames);
    }
  }
});

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <div
            v-else-if="item.key === 'BIO'"
            v-on-clickaway="() => closeIdentityDropdown()"
            class="relative flex items-center h-8 w-full transition-all duration-500 ease-in-out outline outline-1 outline-offset-[-1px] rounded-lg bg-n-alpha-black2"
            :class="{
              'outline-n-weak focus-within:outline-n-brand': isDetailsView,
              'outline-transparent focus-within:outline-n-brand': !isDetailsView
            }"
          >
            <Input
              v-model="identityNumber"
              :placeholder="item.placeholder"
              :message-type="getMessageType(item.key)"
              custom-input-class="!border-0 !outline-none !bg-transparent ltr:!pl-1 rtl:!pr-1 h-8 !py-0.5"
              class="w-full !flex-row"
              @input="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
              @blur="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
            >
              <template #prefix>
                <div class="flex items-center flex-shrink-0">
                  <Button
                    color="slate"
                    size="sm"
                    icon="i-lucide-chevron-down"
                    trailing-icon
                    type="button"
                    class="top-1 ltr:ml-px rtl:mr-px !px-2 outline-0 !outline-none !rounded-lg border-0 ltr:!rounded-r-none rtl:!rounded-l-none !h-[1.875rem]"
                    @click="toggleIdentityDropdown"
                  >
                    <span class="inline-flex justify-center text-sm whitespace-nowrap">
                      {{ identityPrefix.replace('-', '') }}
                    </span>
                  </Button>
                </div>
              </template>
            </Input>
            <DropdownMenu
              v-if="showIdentityDropdown"
              :menu-items="identityOptions"
              class="z-[100] w-24 mt-2 overflow-y-auto ltr:left-0 rtl:right-0 top-full max-h-52"
              @action="onSelectIdentity"
            />
          </div>
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
